import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { parseEther } from 'viem'
import {
  useAccount,
  useConnect,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract
} from 'wagmi'

import ConfirmStakingPopup from '../../components/staking/ConfirmStakingPopup'
import SuccessPopup from '../../components/staking/SuccessPopup'
import {
  DEFAULT_CONTAINER_STATE,
  STAKING_PROCESS_STEP
} from '../../constants/containerStates'
import {
  changeStakingState,
  getUserTier,
  resetStakingState,
  startQBXPricePolling,
  stopQBXPricePolling
} from '../../store/actions/staking'
import { STAKING_PROFILE_DISPATCH } from '../../store/reducers/staking/staking'
import {
  selectStakingProfile,
  selectTokenProfile
} from '../../store/selectors/common'
import { getWalletAddressSelector } from '../../store/selectors/wallet'
import { formatSCAmount, roundDownDecimals } from '../../util/number.helpers'
import { containsNoValues } from '../../util/object.helpers'
import { buildTransactionErrorMessage } from '../../util/rewardBox.helpers'
import {
  CURRENCIES,
  formatQBXCurrency,
  getTxnLog,
  stakingContractConfig,
  tokenContractConfig
} from '../../util/staking/staking.helpers'
import {
  checkValidations,
  requiredRule
} from '../../util/validation/commonValidation.helpers'
import {
  exceedLimitRule,
  validCryptoAmountRule
} from '../../util/validation/validation.helpers'
import { getChain } from '../../util/wallet/wagmiConfig'
import StakingForm from '../../components/stakingForm/StakingForm'

const chainId = getChain().id

const stakingConfig = stakingContractConfig()

const StakingFormContainer = ({
  walletConnectProps: {
    onWalletClick,
    renderWalletPopup,
    stakingContractResponse,
    qbxContractResponse,
    openConnectModal
  },
  t,
  hideWallet
}) => {
  const dispatch = useDispatch()

  const { isConnected } = useAccount()
  const { connect, connectors } = useConnect()

  useEffect(() => {
    const lastConnector = localStorage.getItem('wagmi.connectedConnector')
    if (!isConnected && lastConnector && connectors.length > 0) {
      const connector = connectors.find((c) => c.id === lastConnector)
      if (connector) {
        connect({ connector })
      }
    }
  }, [isConnected, connect, connectors])

  const walletAddressMembership = useSelector(getWalletAddressSelector)
  const tokenProfile = useSelector(selectTokenProfile)
  const stakingProfile = useSelector(selectStakingProfile)
  const connectedWallet = walletAddressMembership.membershipNumber

  const [popupState, setPopupState] = useState({
    id: DEFAULT_CONTAINER_STATE.NONE,
    data: null
  })

  useEffect(() => {
    dispatch(startQBXPricePolling())
    return () => {
      dispatch(stopQBXPricePolling())
    }
  }, [])

  useEffect(() => {
    return () => {
      dispatch(resetStakingState())
    }
  }, [])

  const { switchChain } = useSwitchChain()

  // Stake QBX
  // approve QBX
  const {
    data: approveHash,
    isPending: approvePending,
    error: approveErr,
    writeContract: approveWrite
  } = useWriteContract()

  const { isLoading: approveProcessing, isSuccess: approveConfirmed } =
    useWaitForTransactionReceipt({
      hash: approveHash
    })

  useEffect(() => {
    if (approveErr) {
      updateError({
        stake: buildTransactionErrorMessage(approveErr.shortMessage)
      })
    }
  }, [approveErr])

  useEffect(() => {
    if (approveProcessing || approvePending) {
      updateStakeProcessing(true)
    }
  }, [approveProcessing, approvePending])

  useEffect(() => {
    // Stake: step1 done
    if (approveConfirmed) {
      updateStakeProcessing(true)
      stakeWrite({
        ...stakingConfig,
        functionName: 'stake',
        args: [parseEther(String(stakingProfile.stakeAmount))]
      })
    }
  }, [approveConfirmed])

  // stake Staking
  const {
    data: stakeHash,
    isPending: stakePending,
    error: stakeErr,
    writeContract: stakeWrite
  } = useWriteContract()

  const {
    isLoading: stakeProcessing,
    isSuccess: stakeConfirmed,
    data: stakeReceiptData
  } = useWaitForTransactionReceipt({
    hash: stakeHash
  })

  useEffect(() => {
    if (stakeErr) {
      updateError({
        stake: buildTransactionErrorMessage(stakeErr.shortMessage)
      })
    }
  }, [stakeErr])

  const updateUnStakeProcessing = (isProcessing) => {
    dispatchAction(STAKING_PROFILE_DISPATCH.UN_STAKE_PROCESSING, isProcessing)
  }

  useEffect(() => {
    if (stakeProcessing || stakePending) {
      updateUnStakeProcessing(true)
    }
  }, [stakeProcessing, stakePending])

  useEffect(() => {
    // Stake: step2 done
    if (stakeConfirmed) {
      let claimedAmount = 0
      try {
        const log = getTxnLog(stakeReceiptData?.logs, stakingConfig.address)
        if (log) {
          claimedAmount = formatSCAmount(log.data.slice(2, 66), '0x')
        }
      } catch (e) {
        console.log(e)
      }
      updateError({})
      dispatchAction(STAKING_PROFILE_DISPATCH.STAKE_AMOUNT, '')
      updateStakeProcessing(false)
      setPopupState({
        id: STAKING_PROCESS_STEP.SUCCESS,
        data: {
          type: STAKING_PROCESS_STEP.STAKE,
          value: formatQBXCurrency(claimedAmount)
        }
      })
      setTimeout(() => {
        handleRefreshUserTier()
      }, 75 * 1000) //75 seconds
      stakingContractResponse.refetch()
      qbxContractResponse.refetch()
    }
  }, [stakeConfirmed])

  const [actionType, setActionType] = useState(null)

  useEffect(() => {
    if (isConnected && actionType) {
      if (actionType === STAKING_PROCESS_STEP.STAKE) {
        executeStake()
      }
    }
  }, [isConnected])

  const updateStakeProcessing = (isProcessing) => {
    dispatchAction(STAKING_PROFILE_DISPATCH.STAKE_PROCESSING, isProcessing)
  }

  const updateError = (error) => {
    dispatchAction(STAKING_PROFILE_DISPATCH.ERROR, error)
  }

  const dispatchAction = (property, value) => {
    dispatch(changeStakingState(property, value))
  }

  const handleNoPopup = () => {
    setPopupState({ data: null, id: DEFAULT_CONTAINER_STATE.NONE })
    updateError({})
  }

  const handleRefreshUserTier = () => {
    dispatch(getUserTier())
  }

  const executeStake = () => {
    switchChain({ chainId })
    updateStakeProcessing(true)
    approveWrite({
      ...tokenContractConfig(),
      functionName: 'approve',
      args: [
        stakingConfig.address,
        parseEther(String(stakingProfile.stakeAmount))
      ]
    })
  }

  const handlePopupSubmit = () => {
    switch (popupState.id) {
      case STAKING_PROCESS_STEP.STAKE:
        if (isConnected) {
          setActionType(null)
          executeStake()
        } else {
          setActionType(popupState.id)
          openConnectModal()
        }
        break

      default:
        handleNoPopup()
        break
    }
  }

  const renderPopup = () => {
    switch (popupState.id) {
      case STAKING_PROCESS_STEP.STAKE:
        return (
          <ConfirmStakingPopup
            title='brand:staking.confirm-staking-amount-duration'
            desc='brand:staking.confirm-staking-desc'
            valueDesc={t('brand:staking.staking-duration-months')}
            onSubmit={handlePopupSubmit}
            onCancel={handleNoPopup}
            value={stakingProfile.stakeAmount}
            hideHelpText={false}
            loading={stakingProfile.stakeProcessing}
            error={stakingProfile.error[STAKING_PROCESS_STEP.STAKE]}
            t={t}
            disableClose
          />
        )

      case STAKING_PROCESS_STEP.SUCCESS:
        return (
          <SuccessPopup
            onSubmit={handlePopupSubmit}
            onCancel={handleNoPopup}
            t={t}
            symbol={tokenProfile.symbol}
            {...popupState?.data}
          />
        )

      default:
        break
    }
  }

  const handleStakeSubmit = () => {
    const { stakeAmount, availableQBX } = stakingProfile
    updateError({})
    const errors = checkValidations({
      stakeAmount: {
        value: stakeAmount,
        rules: [
          requiredRule,
          validCryptoAmountRule,
          exceedLimitRule(
            roundDownDecimals(availableQBX),
            t('error.no-enough-points', { tokenSymbol: CURRENCIES.QBX })
          )
        ]
      }
    })
    if (containsNoValues(errors)) {
      setPopupState({ id: STAKING_PROCESS_STEP.STAKE })
    } else {
      updateError(errors)
    }
  }

  return (
    <>
      <StakingForm
        t={t}
        connectedWallet={connectedWallet}
        dispatchAction={dispatchAction}
        stakingProfile={stakingProfile}
        onSubmit={handleStakeSubmit}
        onWalletClick={onWalletClick}
        hideWallet={hideWallet}
      />
      {renderPopup()}
      {renderWalletPopup()}
    </>
  )
}

export default StakingFormContainer
